import React, { Fragment, useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Typography,
  FormGroup,
  Box,
  Divider,
  TextField,
} from "@mui/material";
import {
  ExpandMoreRounded,
  Login as LoginIcon,
  DoorFront as DoorFrontIcon,
  Stairs as StairsIcon,
  AccessibleForward,
  NoMeetingRoom,
} from "@mui/icons-material";
import RequiredErrorBadge from "@/shared/components/RequiredErrorBadge";
import { Option, NoteSectionItem as AccordionItem } from "@/types/common";
import { useAccordion } from "@/hooks/useAccordion";

interface Props {
  data: AccordionItem[];
  hasError: boolean;
  onSelectionChange: (selected: { [accordionId: string]: Option[] }) => void;
  defaultValues: { [key: string]: Option[] } | null;
  isSubmitting: boolean;
  noteErrors?: Record<string, boolean>;
  tagsError?: boolean;
}

const NotesSection: React.FC<Props> = ({
  data = [],
  hasError = false,
  onSelectionChange,
  defaultValues,
  isSubmitting,
  noteErrors,
}) => {
  const getSectionIcon = (header: string) => {
    if (header.includes("Entrance to building")) return <LoginIcon sx={{ color: "#9D79F4", fontSize: 18 }} />;
    if (header.includes("Ramp")) return <AccessibleForward sx={{ color: "#9D79F4", fontSize: 18 }} />;
    if (header.includes("door")) return <DoorFrontIcon sx={{ color: "#9D79F4", fontSize: 18 }} />;
    if (header.includes("Steps")) return <StairsIcon sx={{ color: "#9D79F4", fontSize: 18 }} />;
    if (header.includes("Entrance not on")) return <NoMeetingRoom sx={{ color: "#9D79F4", fontSize: 18 }} />;
    return <LoginIcon sx={{ color: "#9D79F4", fontSize: 18 }} />;
  };

  const [selectedValues, setSelectedValues] = useState<{
    [key: string]: Option[];
  }>({});
  const { expanded, handleAccordionChange } = useAccordion([]);

  useEffect(() => {
    if (defaultValues) {
      setSelectedValues(defaultValues);
    }
  }, [defaultValues]);

  const handleCheckboxChange = (
    accordionId: string,
    option: Option,
    checked: boolean
  ) => {
    const existing = selectedValues[accordionId] || [];
    const updatedSection = checked
      ? [...existing, option]
      : existing.filter((item) => item.notes_data_id !== option.notes_data_id);

    const updated = { ...selectedValues, [accordionId]: updatedSection };
    setSelectedValues(updated);

    // Only hold off notifying the parent while freshly checking "Other" with
    // no text typed yet — unchecking (removing) must always propagate.
    const isFreshlyCheckedOther =
      checked &&
      option?.notes === "Other - type your own observation" &&
      !option?.other_notes;

    if (!isFreshlyCheckedOther) {
      onSelectionChange(updated);
    }
  };

  const handleTextChange = (
    accordionId: string,
    option: Option,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newOtherNotes = event.target.value;

    let updatedSection = (selectedValues[accordionId] || []).map((item) =>
      item.notes_data_id === option.notes_data_id
        ? { ...item, other_notes: newOtherNotes }
        : item
    );

    if (
      newOtherNotes === "" &&
      option?.notes === "Other - type your own observation"
    ) {
      updatedSection = updatedSection.filter(
        (item) => item.notes_data_id !== option.notes_data_id
      );
    }

    const updated = { ...selectedValues, [accordionId]: updatedSection };
    setSelectedValues(updated);
    onSelectionChange(updated);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {data?.map(({ sectionHeader, followUpQuestion, notes, isMandatory }) => {
        const hasSelectedValue = (selectedValues[sectionHeader] || []).some(
          (item) =>
            !(item.notes === "Other - type your own observation" && !item.other_notes)
        );
        const isExpanded = expanded.includes(sectionHeader);
        return (
          <Accordion
            key={followUpQuestion}
            disableGutters
            disabled={isSubmitting}
            sx={{
              mb: "2.5%",
              borderRadius: "8px !important",
              border: `1px solid ${
                !isExpanded && hasSelectedValue ? "#9443DF" : "#D9D9D9"
              }`,
              boxShadow: "none",
              outline: "none",
              background:
                !isExpanded && hasSelectedValue
                  ? "#F0DEFF !important"
                  : "transparent !important",
            }}
            onChange={handleAccordionChange(sectionHeader)}
            slotProps={{
              root: {
                style: {
                  backgroundColor: "transparent",
                },
              },
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreRounded
                  fontSize="medium"
                  sx={{ color: "#626262" }}
                />
              }
            >
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%", gap: "12px" }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    backgroundColor: "#F0DEFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {getSectionIcon(sectionHeader)}
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: "3.4vw", sm: 14 },
                    display: "flex",
                    alignItems: "center",
                    color: "#191919",
                  }}
                >
                  {sectionHeader}
                  {isMandatory && <span style={{ color: "red" }}>*</span>}
                </Typography>
                {hasError && isMandatory && !hasSelectedValue ? (
                  <RequiredErrorBadge sx={{ ml: "auto", mr: 1 }} />
                ) : (
                  <></>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {noteErrors?.[sectionHeader] && (
                <Typography sx={{ color: "#d32f2f", fontSize: "12px", fontWeight: "bold", mb: "2%" }}>
                  Not Approved
                </Typography>
              )}
              <Typography sx={{ fontSize: { xs: "3.4vw", sm: 14 } }}>
                {followUpQuestion}
              </Typography>
              <Divider sx={{ color: "#D9D9D9", m: "4% 0 2% 0" }} />
              <FormGroup>
                {notes?.map((option) => {
                  const checked =
                    selectedValues[sectionHeader]?.some(
                      (item) => item.notes_data_id === option.notes_data_id
                    ) || false;

                  return (
                    <Fragment key={option?.notes}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          columnGap: "1vw",
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={isSubmitting}
                          onChange={(e) =>
                            handleCheckboxChange(
                              sectionHeader,
                              option,
                              e.target.checked
                            )
                          }
                          sx={{
                            p: "1%",
                          }}
                        />
                        <Box
                          sx={{
                            fontSize: { xs: "3.4vw", sm: 14 },
                          }}
                        >
                          {option?.notes}
                        </Box>
                      </Box>

                      {checked &&
                        option?.notes ===
                          "Other - type your own observation" && (
                            <TextField
                              placeholder="Type your own observation"
                              variant="outlined"
                              size="small"
                              fullWidth
                              value={
                                selectedValues[sectionHeader]?.find(
                                  (item) =>
                                    item.notes_data_id === option.notes_data_id
                                )?.other_notes || ""
                              }
                              onChange={(e) =>
                                handleTextChange(sectionHeader, option, e)
                              }
                              error={
                                hasError &&
                                !selectedValues[sectionHeader]?.find(
                                  (item) => item.notes_data_id === option.notes_data_id
                                )?.other_notes
                              }
                              helperText={
                                hasError &&
                                !selectedValues[sectionHeader]?.find(
                                  (item) => item.notes_data_id === option.notes_data_id
                                )?.other_notes
                                  ? "Please type your observation"
                                  : ""
                              }
                              sx={{
                                mt: "1%",
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#fff",
                                borderRadius: "8px",
                                fontSize: "16px",
                                color: "#939393",
                                "& input::placeholder": {
                                  color: "#939393",
                                  fontSize: { xs: "14px", sm: "16px" },
                                  opacity: 1,
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                color: "#939393",
                              },
                            }}
                          />
                        )}
                    </Fragment>
                  );
                })}
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default NotesSection;
